import { useEffect, useState } from "react";
import { PrismBanner, PrismButton, PrismCheckbox, PrismDialog, PrismInputText } from "@prism-ui/react";
import type { Chapter } from "./ProjectFairs";
import { anchorError as computeAnchorError, slugifyAnchor, slugifyAnchorWhileTyping } from "./anchor";

interface NewEventModalProps {
  show: boolean;
  year: number;
  chapters: Chapter[];
  defaultChapterId: number | null;
  creating: boolean;
  /** Anchors already in use for this year. Anchors are unique per year, so these are the
   *  values the server would reject with a 409. */
  takenAnchors: string[];
  /** Server-side failure from the last create attempt, shown in the dialog itself. */
  error?: string;
  onCreate: (payload: { title: string; chapter_key: string; chapter_ids: number[] }) => void;
  onCancel: () => void;
}

// Mints an additional named event for the year — a joint virtual fair, a hand-curated expo,
// or any second event for a chapter that already has one. Distinct from the original
// "one default fair per chapter" flow: this always creates a new row (the anchor is
// required precisely so it never collides with an existing default event).
const NewEventModal = ({
  show,
  year,
  chapters,
  defaultChapterId,
  creating,
  takenAnchors,
  error,
  onCreate,
  onCancel,
}: NewEventModalProps) => {
  const [title, setTitle] = useState("");
  const [anchor, setAnchor] = useState("");
  const [ownerIds, setOwnerIds] = useState<number[]>([]);
  // Until the officer edits the anchor themselves it tracks the title. Once they touch it
  // the title stops overwriting their choice — retyping the title should not clobber a
  // deliberately hand-picked anchor like "expo".
  const [anchorTouched, setAnchorTouched] = useState(false);

  useEffect(() => {
    if (!show) return;
    setTitle("");
    setAnchor("");
    setAnchorTouched(false);
    setOwnerIds(defaultChapterId ? [defaultChapterId] : []);
  }, [show, defaultChapterId]);

  const toggleOwner = (chapterId: number, checked: boolean) => {
    setOwnerIds((prev) => (checked ? [...new Set([...prev, chapterId])] : prev.filter((id) => id !== chapterId)));
  };

  const finalAnchor = slugifyAnchor(anchor);
  const anchorError = computeAnchorError(anchor, year, takenAnchors);

  const titleError = title.length > 0 && !title.trim() ? "Enter a title." : "";

  const canCreate = !!title.trim() && !!finalAnchor && !anchorError && ownerIds.length > 0;

  const handleTitle = (value: string) => {
    setTitle(value);
    if (!anchorTouched) setAnchor(slugifyAnchorWhileTyping(value));
  };

  const handleAnchor = (value: string) => {
    setAnchorTouched(true);
    setAnchor(slugifyAnchorWhileTyping(value));
  };

  return (
    <PrismDialog open={show} close-method="user" onPrismClose={onCancel} setAriaLabel="New Project Fair Event">
      <span slot="header">New Project Fair Event ({year})</span>
      <div className="project-fairs-modal-body">
        <p className="project-fairs-window-anchor">
          Use this for an event that isn&apos;t a single chapter&apos;s default fair — a joint virtual fair, a
          hand-curated expo, or a second in-person event for one chapter. Pick every chapter that should be able to
          edit it.
        </p>
        <PrismInputText
          label="Title"
          value={title}
          invalid={!!titleError}
          invalidMessage={titleError}
          onPrismInput={(e: CustomEvent<{ value: string }>) => handleTitle(e.detail?.value || "")}
        />
        <PrismInputText
          label="Anchor"
          hint="Lowercase letters, numbers and hyphens. Generated from the title until you edit it."
          value={anchor}
          invalid={!!anchorError}
          invalidMessage={anchorError}
          onPrismInput={(e: CustomEvent<{ value: string }>) => handleAnchor(e.detail?.value || "")}
        />
        {/* The anchor only means anything as part of a URL, so show the URL rather than the
            bare slug — it also makes the "#" concrete instead of a hint in the label. */}
        <p className="project-fairs-anchor-preview">
          {finalAnchor ? (
            <>
              Public link: <code>/project-fair/{year}#{finalAnchor}</code>
            </>
          ) : (
            <>
              Public link: <code>/project-fair/{year}#…</code>
            </>
          )}
        </p>
        <fieldset className="project-fairs-owner-chapters">
          <legend>Owning chapters</legend>
          {chapters.map((chapter) => (
            <PrismCheckbox
              key={chapter.id}
              label={chapter.location}
              checked={ownerIds.includes(chapter.id)}
              onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => toggleOwner(chapter.id, !!e.detail?.checked)}
            />
          ))}
          {!ownerIds.length ? (
            <p className="project-fairs-owner-error">Pick at least one owning chapter.</p>
          ) : null}
        </fieldset>
        {error ? <PrismBanner tone="danger">{error}</PrismBanner> : null}
      </div>

      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={onCancel}>
        Cancel
      </PrismButton>
      <PrismButton
        slot="footer"
        tone="primary-brand"
        display="fill"
        disabled={creating || !canCreate}
        loading={creating}
        onPrismClick={() => onCreate({ title: title.trim(), chapter_key: finalAnchor, chapter_ids: ownerIds })}
      >
        Create
      </PrismButton>
    </PrismDialog>
  );
};

export default NewEventModal;
